import { showNotification } from "#/src/utils/notification";
import { notifyNetworkFailure } from "#/src/utils/networkFailure";
import { logNetworkFailure } from "#/src/utils/networkFailureLog";
import { isNetworkFailure } from "#/src/utils/apiError";
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import axiosRateLimit from "#/src/utils/AxiosRateLimiter";
import * as logClient from "@classdojo/log-client";
import { isPrecanaryRequested } from "@web-monorepo/fetchers";

declare module "axios" {
  interface AxiosRequestConfig {
    /** The caller renders a local retry state instead of a global network toast. */
    silentNetworkError?: boolean;
  }
}

// Same symbol as DOJO_FETCH_MARKER in telemetry's patchFetch: a request carrying it is not
// reported as a BareFetchError. errorHandler below logs response errors and NETWORK_ERROR_CODES
// instead (except on SILENT_NETWORK_ERROR_URLS, see logAxiosNetworkError); anything else without a
// response (an ERR_CANCELED abort) is deliberately unreported.
export const HANDLED_FETCH_MARKER = Symbol.for("dojo.fetch.handled");

const fetchClient = ({ prefixPath }: { prefixPath: string }) => {
  const defaultOptions = {
    baseURL: prefixPath,
    method: "get",
    headers: {
      "Content-Type": "application/json",
      "x-sign-attachment-urls": "true",
    },
    adapter: "fetch",
    // fetchOptions becomes the RequestInit passed to fetch.
    fetchOptions: { [HANDLED_FETCH_MARKER]: true },
    withCredentials: true,
    credentials: "same-origin",
  };

  return axios.create(defaultOptions);
};

// Requests that must never surface a network-error toast: /logout (the user
// is leaving anyway) and the tutor activity heartbeat (a background ping that
// fires every minute — a toast would blame the user for something they never
// did; it self-corrects on the next tick).
const SILENT_NETWORK_ERROR_URLS = ["/logout", "/tutor/activity"];

const isSilentUrl = (error: AxiosError) => SILENT_NETWORK_ERROR_URLS.includes(error.config?.url ?? "");

const MAX_LOG_ARRAY_ITEMS = 10;
const MAX_LOG_OBJECT_KEYS = 25;
const SENSITIVE_LOG_KEY_PATTERN =
  /authorization|cookie|email|(?:first|last|display|full|parent|student|teacher|tutor|user)_?name|password|phone|secret|token|\bname\b/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function sanitizeForLog(value: unknown, depth = 0): unknown {
  if (value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (depth >= 3) {
    return "[truncated]";
  }

  if (Array.isArray(value)) {
    const result = value.slice(0, MAX_LOG_ARRAY_ITEMS).map((item) => sanitizeForLog(item, depth + 1));
    const remainingItems = value.length - MAX_LOG_ARRAY_ITEMS;
    return remainingItems > 0
      ? [...result, `[truncated ${remainingItems} more item${remainingItems === 1 ? "" : "s"}]`]
      : result;
  }

  if (!isRecord(value)) {
    return String(value);
  }

  const entries = Object.entries(value);
  const result = Object.fromEntries(
    entries
      .slice(0, MAX_LOG_OBJECT_KEYS)
      .map(([key, nestedValue]) => [
        key,
        SENSITIVE_LOG_KEY_PATTERN.test(key) ? "[redacted]" : sanitizeForLog(nestedValue, depth + 1),
      ]),
  );

  if (entries.length > MAX_LOG_OBJECT_KEYS) {
    const remainingKeys = entries.length - MAX_LOG_OBJECT_KEYS;
    result.__truncated = `${remainingKeys} more key${remainingKeys === 1 ? "" : "s"}`;
  }

  return result;
}

function getRequestPath(error: AxiosError) {
  const baseURL = error.config?.baseURL;
  const url = error.config?.url;
  if (!baseURL) return url;
  if (!url) return baseURL;
  return `${baseURL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

function getResponseErrorData(error: AxiosError) {
  const responseData = error.response?.data;
  if (isRecord(responseData) && "error" in responseData) {
    return responseData.error;
  }
  return responseData;
}

// SILENT_NETWORK_ERROR_URLS are skipped because their failures are expected (the heartbeat would
// log once a minute while offline).
function logAxiosNetworkError(error: AxiosError) {
  if (!isNetworkFailure(error)) return;
  if (isSilentUrl(error)) return;

  logNetworkFailure({
    method: error.config?.method?.toUpperCase(),
    requestPath: getRequestPath(error),
    errorCode: error.code,
    errorName: error.name,
    errorMessage: error.message,
  });
}

function logAxiosResponseError(error: AxiosError) {
  if (!error.response) return;

  void logClient.logMessage("ERROR", "marketplace.axios_request_failed", {
    site: "marketplace",
    statusCode: error.response.status,
    method: error.config?.method?.toUpperCase(),
    url: error.config?.url,
    baseURL: error.config?.baseURL,
    requestPath: getRequestPath(error),
    requestParams: sanitizeForLog(error.config?.params),
    responseError: sanitizeForLog(getResponseErrorData(error)),
    errorCode: error.code,
    errorName: error.name,
    errorMessage: error.message,
  });
}

export function errorHandler(error: AxiosError, notify: typeof showNotification = showNotification) {
  logAxiosResponseError(error);
  logAxiosNetworkError(error);

  // silentNetworkError skips only the toast (the log above still runs, as on fetchers); the silent URLs skip both.
  const isSilent = error.config?.silentNetworkError || isSilentUrl(error);
  if (error?.code === "ERR_NETWORK" && !isSilent) {
    notifyNetworkFailure("offline", notify);
  } else if (error?.code === "ECONNABORTED" && !isSilent) {
    notifyNetworkFailure("timeout", notify);
  }

  return Promise.reject(error);
}

export function addPrecanaryQueryParam(config: InternalAxiosRequestConfig) {
  if (isPrecanaryRequested()) {
    config.params = { ...config.params, usePrecanary: "true" };
  }
  return config;
}

const monolithApiClient = fetchClient({
  prefixPath: "/api/marketplace",
});

monolithApiClient.interceptors.request.use(addPrecanaryQueryParam);

axiosRateLimit(monolithApiClient, errorHandler);

const featureSwitchApiClient = fetchClient({
  prefixPath: "/api",
});

axiosRateLimit(featureSwitchApiClient, errorHandler);

export { featureSwitchApiClient, monolithApiClient };
